// ---------------------------------------------------------------------------
// What a model was thinking, in a dialog.
//
// **It used to be an overlay pinned inside the frame it was opened from, and
// that was two different things wearing one implementation.** `absolute inset-0`
// covers whatever the nearest positioned ancestor happens to be, and the two
// call sites do not have the same one: from `AnswerFrame` it filled a frame
// holding one answer, and from a leaderboard row it filled the whole standing
// panel. On a desktop both are large enough that "the panel is now the
// reasoning" reads as deliberate. On a phone the panel is a few hundred pixels
// inside a fixed-viewport screen, so it covered part of the screen, left the
// running-order rail and the control bar showing round it, and looked like a
// half-finished takeover rather than a thing that had opened. Isaac reported
// exactly that, and reported the way out as the real problem.
//
// **The way out was one `label-mono` word.** No padding, so about a ten-pixel
// tap target in the corner of a panel, and it was the ONLY way out: no scrim to
// tap, no Escape, no dialog semantics, nothing. A phone user who opened it by
// accident had one small target to find, and the reveal was still moving
// underneath them while they looked for it.
//
// So it is a dialog now, and everything that was missing is the point of the
// change rather than trimming round the edges:
//
//   - **Portalled to the body.** No ancestor's overflow, stacking context or
//     positioning can clip it, and it is the same size and in the same place
//     whichever surface opened it. That was the actual defect.
//   - **Four ways out**, because the complaint was about getting out: the close
//     button, the scrim, Escape, and the backdrop area round the card. The
//     button is a real target — `min-h-11`, about 44px — rather than a word.
//   - **A bottom sheet on a phone, a centred card above `sm`.** Same shape as
//     `EditPlayerSheet`, which exists for the same reason: on a phone the thing
//     you have to reach belongs under the thumb, not in a far corner.
//   - **Focus moves in and comes back.** Opening moves focus to the close
//     button, so the way out is the first thing a keyboard or a screen reader
//     meets; closing returns it to the chip that opened it, so the reveal is not
//     left with focus on the body.
//   - **The reveal's arrow stepping does not fire while it is open.** That
//     listener is on the window, and the keydown handler here stops the event
//     before it gets there — otherwise reading a model's reasoning would step
//     the reveal underneath the dialog.
//
// `z-[35]`, beside `EditPlayerSheet`: above the chat drawer, and below the
// disconnect sheet and the server pause, because a dropped connection or a
// stopped clock is news about the round and this is something to read.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import type { PublicPlayer } from '@countdown/shared';
import { Button } from '../../../components/primitives/Button.js';
import { swallowNextClick } from '../../../lib/swallowNextClick.js';

export function ReasoningModal({
  player,
  reasoning,
  onClose,
}: {
  player: PublicPlayer;
  reasoning: string;
  onClose: () => void;
}): ReactElement {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[35] flex items-end justify-center p-4 sm:items-center"
      style={{ background: 'var(--c-scrim)' }}
      // Tapping the backdrop closes it. Guarded on the target being the
      // backdrop itself, or a tap that started on the card and ended on a
      // scroll would close the thing being read.
      //
      // `swallowNextClick` for the same reason the host row menu needs it:
      // closing on the press unmounts this backdrop before the finger comes up,
      // so the browser hit-tests the click that follows against whatever is
      // underneath — on the reveal that is the running order, the chevrons and
      // the control bar. A tap meant as "get me out" must not also step the
      // slide or hold the reveal.
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        swallowNextClick();
        onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reasoning-title"
        className="sheet-up flex max-h-[80vh] w-full max-w-[420px] flex-col gap-3 rounded-frame border border-line bg-surface p-4 sm:p-5"
        // The reveal steps on the arrow keys from a window listener, and a
        // window listener is the last thing a bubbling event reaches — so
        // stopping the arrows here is what keeps reading a model's reasoning
        // from moving the slide behind the dialog.
        //
        // **Only the arrows.** The first cut stopped every key, which took
        // Escape with it: the Escape handler is on the document, the document is
        // on the way to the window, and the way out of the dialog was blocked by
        // the dialog. The test for Escape is what caught it.
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') event.stopPropagation();
        }}
      >
        <div className="flex items-baseline gap-2">
          <h2 id="reasoning-title" className="min-w-0 truncate text-[17px] font-bold">
            {player.displayName} was thinking
          </h2>
          {/* The model, where there is one. It is the fact that makes the text
              below worth reading: the same reasoning from a 3B model and from a
              frontier one are different things to look at. */}
          {player.botModel === null ? null : (
            <span className="label-mono shrink-0 truncate text-ink-dim">{player.botModel}</span>
          )}
        </div>

        {/* The scroll is inside the card, so the page behind it never moves —
            the reveal is a fixed-viewport screen and must not gain a scrollbar.
            `overscroll-contain` stops a flick at the end of the text handing the
            scroll to whatever is underneath. */}
        <p className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-left text-[13px] leading-relaxed whitespace-pre-wrap text-ink-dim">
          {reasoning}
        </p>

        {/* Full width and a real height — `Button`'s smallest size is already
            44px. This is the control the whole change is about: it used to be
            the word "close" with no padding, in a corner.

            `autoFocus` rather than a ref, because `Button` is a plain function
            component and `autoFocus` rides the same prop spread every other
            attribute does. It is the way OUT taking focus, which is the one
            thing a dialog of unreadable length owes a keyboard. */}
        <Button autoFocus variant="outline" block onClick={onClose}>
          Close
        </Button>
      </div>
    </div>,
    document.body,
  );
}
